import { ApolloError } from '@apollo/client';
import { CollapsibleChart } from '@e2m-customer-portal/domains';
import { ErrorComponent } from '@e2m-customer-portal/ui';
import { DatapointCollection, Device, DeviceTimeSeries, Forecast, Interval } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { ControlEnergyChart } from './ControlEnergyChart';
import { PowerFeedChart } from './PowerFeedChart';
import { StateOfChargeChart } from './StateOfChargeChart';
import { BatteryPowerType } from './utils';
import { EChartsType } from 'echarts';
import { EChartsReactRef } from 'react-echarts-library';

interface BatteryChartsProps {
  device: Device;
  dataInterval: Interval;
  forecasts: [readonly Forecast[] | undefined, ApolloError | undefined];
  acceptedForecasts: [DatapointCollection | undefined, ApolloError | undefined];
  deviceTimeSeries: [readonly DeviceTimeSeries[] | undefined, ApolloError | undefined];
  batteriesPower: Map<string, BatteryPowerType>;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function BatteryCharts({
  device,
  dataInterval,
  forecasts,
  acceptedForecasts,
  deviceTimeSeries,
  batteriesPower,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<BatteryChartsProps>) {
  const [t] = useTranslation();

  const [power, setPower] = useState<BatteryPowerType | undefined>(batteriesPower.get(device.internalId));

  useEffect(() => {
    if (batteriesPower) {
      setPower(batteriesPower.get(device.internalId));
    }
  }, [batteriesPower]);

  return (
    <>
      <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_MARKET_ALLOCATION')}>
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <ControlEnergyChart
            dataInterval={dataInterval}
            device={device}
            forecasts={forecasts[0]}
            forecastsError={forecasts[1]}
            acceptedForecasts={acceptedForecasts[0]}
            acceptedForecastError={acceptedForecasts[1]}
            power={power}
            onChartReadyCallback={onChartReadyCallback}
            resetZoom={resetZoom}
            isZoomed={isZoomed}
          />
        </ErrorBoundary>
      </CollapsibleChart>

      <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_POWER_FEED')}>
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <PowerFeedChart
            dataInterval={dataInterval}
            device={device}
            forecasts={forecasts[0]}
            forecastsError={forecasts[1]}
            power={power}
            deviceTimeSeries={deviceTimeSeries[0] && [...deviceTimeSeries[0]]}
            deviceTimeSeriesError={deviceTimeSeries[1]}
            onChartReadyCallback={onChartReadyCallback}
            resetZoom={resetZoom}
            isZoomed={isZoomed}
          />
        </ErrorBoundary>
      </CollapsibleChart>
      <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_STATE_OF_CHARGE')}>
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <StateOfChargeChart
            dataInterval={dataInterval}
            device={device}
            deviceTimeSeries={deviceTimeSeries[0] && [...deviceTimeSeries[0]]}
            deviceTimeSeriesError={deviceTimeSeries[1]}
            onChartReadyCallback={onChartReadyCallback}
            resetZoom={resetZoom}
            isZoomed={isZoomed}
          />
        </ErrorBoundary>
      </CollapsibleChart>
    </>
  );
}
